import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Star, 
  MessageCircle, 
  Reply, 
  Filter, 
  Search,
  ThumbsUp,
  ThumbsDown,
  Calendar,
  User,
  Clock,
  Send,
  X,
  MoreHorizontal,
  Scale
} from 'lucide-react';
import toast from 'react-hot-toast';
// REMOVED: Sample data imports - now using real data only

const LawyerRatingsReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, 5star, 4star, 3star, 2star, 1star
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent, rating, oldest

  // Statistics
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });

  const translations = {
    english: {
      title: 'Ratings & Reviews',
      subtitle: 'Manage your client feedback',
      totalReviews: 'Total Reviews',
      averageRating: 'Average Rating',
      filterAll: 'All Reviews',
      filter5Star: '5 Star',
      filter4Star: '4 Star',
      filter3Star: '3 Star',
      filter2Star: '2 Star',
      filter1Star: '1 Star',
      sortRecent: 'Most Recent',
      sortRating: 'Highest Rating',
      sortOldest: 'Oldest First',
      searchPlaceholder: 'Search reviews...',
      noReviews: 'No reviews yet',
      noReviewsDesc: 'Client reviews will appear here once they rate your services',
      reply: 'Reply',
      replyPlaceholder: 'Write your reply...',
      sendReply: 'Send Reply',
      cancel: 'Cancel',
      deleteReply: 'Delete Reply',
      editReply: 'Edit Reply',
      helpful: 'Helpful',
      notHelpful: 'Not Helpful',
      reported: 'Reported',
      reportReview: 'Report Review',
      reviewDate: 'Review Date',
      clientName: 'Client Name',
      consultationType: 'Consultation Type',
      rating: 'Rating',
      feedback: 'Feedback',
      replyToReview: 'Reply to Review',
      yourReply: 'Your Reply',
      noReply: 'No reply yet',
      replySent: 'Reply sent successfully!',
      replyDeleted: 'Reply deleted successfully!',
      replyUpdated: 'Reply updated successfully!',
      errorSendingReply: 'Error sending reply. Please try again.',
      errorDeletingReply: 'Error deleting reply. Please try again.',
      errorUpdatingReply: 'Error updating reply. Please try again.',
      confirmDeleteReply: 'Are you sure you want to delete this reply?',
      confirmReportReview: 'Are you sure you want to report this review?',
      reportReason: 'Report Reason',
      reportReasonPlaceholder: 'Please specify the reason for reporting...',
      submitReport: 'Submit Report',
      reportSubmitted: 'Review reported successfully!',
      errorReporting: 'Error reporting review. Please try again.'
    },
    hindi: {
      title: 'रेटिंग और समीक्षाएं',
      subtitle: 'अपने ग्राहक प्रतिक्रिया का प्रबंधन करें',
      totalReviews: 'कुल समीक्षाएं',
      averageRating: 'औसत रेटिंग',
      filterAll: 'सभी समीक्षाएं',
      filter5Star: '5 सितारे',
      filter4Star: '4 सितारे',
      filter3Star: '3 सितारे',
      filter2Star: '2 सितारे',
      filter1Star: '1 सितारा',
      sortRecent: 'सबसे नई',
      sortRating: 'उच्चतम रेटिंग',
      sortOldest: 'सबसे पुरानी',
      searchPlaceholder: 'समीक्षाएं खोजें...',
      noReviews: 'अभी तक कोई समीक्षा नहीं',
      noReviewsDesc: 'ग्राहक समीक्षाएं यहां दिखाई देंगी जब वे आपकी सेवाओं को रेट करेंगे',
      reply: 'जवाब दें',
      replyPlaceholder: 'अपना जवाब लिखें...',
      sendReply: 'जवाब भेजें',
      cancel: 'रद्द करें',
      deleteReply: 'जवाब हटाएं',
      editReply: 'जवाब संपादित करें',
      helpful: 'उपयोगी',
      notHelpful: 'उपयोगी नहीं',
      reported: 'रिपोर्ट किया गया',
      reportReview: 'समीक्षा रिपोर्ट करें',
      reviewDate: 'समीक्षा तिथि',
      clientName: 'ग्राहक का नाम',
      consultationType: 'परामर्श प्रकार',
      rating: 'रेटिंग',
      feedback: 'प्रतिक्रिया',
      replyToReview: 'समीक्षा का जवाब दें',
      yourReply: 'आपका जवाब',
      noReply: 'अभी तक कोई जवाब नहीं',
      replySent: 'जवाब सफलतापूर्वक भेजा गया!',
      replyDeleted: 'जवाब सफलतापूर्वक हटा दिया गया!',
      replyUpdated: 'जवाब सफलतापूर्वक अपडेट किया गया!',
      errorSendingReply: 'जवाब भेजने में त्रुटि। कृपया पुनः प्रयास करें।',
      errorDeletingReply: 'जवाब हटाने में त्रुटि। कृपया पुनः प्रयास करें।',
      errorUpdatingReply: 'जवाब अपडेट करने में त्रुटि। कृपया पुनः प्रयास करें।',
      confirmDeleteReply: 'क्या आप वाकई इस जवाब को हटाना चाहते हैं?',
      confirmReportReview: 'क्या आप वाकई इस समीक्षा को रिपोर्ट करना चाहते हैं?',
      reportReason: 'रिपोर्ट का कारण',
      reportReasonPlaceholder: 'कृपया रिपोर्ट करने का कारण बताएं...',
      submitReport: 'रिपोर्ट सबमिट करें',
      reportSubmitted: 'समीक्षा सफलतापूर्वक रिपोर्ट की गई!',
      errorReporting: 'समीक्षा रिपोर्ट करने में त्रुटि। कृपया पुनः प्रयास करें।'
    }
  };

  const t = translations.english; // You can make this dynamic based on language preference

  // Load reviews from Firestore
  useEffect(() => {
    loadReviews();
  }, []);

  // Filter and sort reviews when filter, search, or sort changes
  useEffect(() => {
    filterAndSortReviews();
  }, [reviews, filter, searchTerm, sortBy]);

  const loadReviews = async () => {
    try {
      // Import the rating service
      const { ratingService } = await import('../services/firebaseService');
      
      // Get current user (lawyer)
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const lawyerId = currentUser.uid || currentUser.id;
      
      if (!lawyerId) {
        console.error('No lawyer ID found');
        setReviews([]);
        return;
      }

      // Get ratings from Firestore
      const result = await ratingService.getLawyerRatings(lawyerId);
      
      if (result.success) {
        setReviews(result.data);
        calculateStats(result.data);
      } else {
        console.error('Error loading reviews:', result.error);
        setReviews([]);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]);
    }
  };

  const calculateStats = (reviewsData) => {
    if (reviewsData.length === 0) {
      setStats({
        totalReviews: 0,
        averageRating: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
      });
      return;
    }

    const totalReviews = reviewsData.length;
    const totalRating = reviewsData.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = totalRating / totalReviews;

    const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviewsData.forEach(review => {
      ratingDistribution[review.rating]++;
    });

    setStats({
      totalReviews,
      averageRating: parseFloat(averageRating.toFixed(1)),
      ratingDistribution
    });
  };

  const filterAndSortReviews = () => {
    let filtered = [...reviews];

    // Apply filter
    if (filter !== 'all') {
      const rating = parseInt(filter.replace('star', ''));
      filtered = filtered.filter(review => review.rating === rating);
    }

    // Apply search
    if (searchTerm.trim()) {
      filtered = filtered.filter(review => 
        review.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.feedback?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.consultationType?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'oldest':
          return new Date(a.timestamp) - new Date(b.timestamp);
        case 'recent':
        default:
          return new Date(b.timestamp) - new Date(a.timestamp);
      }
    });

    setFilteredReviews(filtered);
  };

  const handleReply = (review) => {
    setSelectedReview(review);
    setReplyText(review.lawyerReply || '');
    setShowReplyModal(true);
  };

  const submitReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setIsSubmitting(true);
    try {
      // Import the rating service
      const { ratingService } = await import('../services/firebaseService');
      
      // Submit reply to Firestore
      const result = await ratingService.addLawyerReply(selectedReview.id, replyText.trim());
      
      if (result.success) {
        // Update local state
        const updatedReviews = reviews.map(review => {
          if (review.id === selectedReview.id) {
            return {
              ...review,
              lawyerReply: replyText.trim(),
              lawyerReplyDate: new Date().toISOString()
            };
          }
          return review;
        });

        setReviews(updatedReviews);
        
        toast.success(t.replySent);
        setShowReplyModal(false);
        setSelectedReview(null);
        setReplyText('');
      } else {
        throw new Error(result.error || 'Failed to submit reply');
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error(t.errorSendingReply);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteReply = async (reviewId) => {
    if (!confirm(t.confirmDeleteReply)) return;

    try {
      // Import the rating service
      const { ratingService } = await import('../services/firebaseService');
      
      // Delete reply from Firestore
      const result = await ratingService.deleteLawyerReply(reviewId);
      
      if (result.success) {
        // Update local state
        const updatedReviews = reviews.map(review => {
          if (review.id === reviewId) {
            const { lawyerReply, lawyerReplyDate, ...rest } = review;
            return rest;
          }
          return review;
        });

        setReviews(updatedReviews);
        
        toast.success(t.replyDeleted);
      } else {
        throw new Error(result.error || 'Failed to delete reply');
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      toast.error(t.errorDeletingReply);
    }
  };

  const reportReview = async (review) => {
    const reason = prompt(t.reportReasonPlaceholder);
    if (!reason) return;

    try {
      const updatedReviews = reviews.map(r => {
        if (r.id === review.id) {
          return {
            ...r,
            reported: true,
            reportReason: reason,
            reportDate: new Date().toISOString()
          };
        }
        return r;
      });

      setReviews(updatedReviews);
      localStorage.setItem('lawyerRatings', JSON.stringify(updatedReviews));
      
      toast.success(t.reportSubmitted);
    } catch (error) {
      console.error('Error reporting review:', error);
      toast.error(t.errorReporting);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3 h-3 ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-xs font-medium text-gray-600 ml-1">
          {rating}/5
        </span>
      </div>
    );
  };

  const renderRatingDistribution = () => {
    return (
      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((stars) => (
          <div key={stars} className="flex items-center gap-2">
            <div className="flex items-center gap-1 w-12">
              <span className="text-xs font-medium">{stars}</span>
              <Star className="w-2.5 h-2.5 text-yellow-400 fill-current" />
            </div>
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-yellow-400 h-1.5 rounded-full"
                style={{
                  width: `${stats.totalReviews > 0 ? (stats.ratingDistribution[stars] / stats.totalReviews) * 100 : 0}%`
                }}
              />
            </div>
            <span className="text-xs text-gray-600 w-6 text-right">
              {stats.ratingDistribution[stars]}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">{t.title}</h1>
              <p className="text-sm text-gray-500 truncate">{t.subtitle}</p>
            </div>
            {/* Development buttons - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    // REMOVED: Sample reviews generation - now using real data only
                    loadReviews();
                    toast.success('Sample reviews added!');
                  }}
                  className="px-2 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Data
                </button>
                <button
                  onClick={() => {
                    clearSampleReviews();
                    loadReviews();
                    toast.success('Sample reviews cleared!');
                  }}
                  className="px-2 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Statistics Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                <Star className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-xs text-gray-500 mb-1">{t.totalReviews}</p>
              <p className="text-lg font-bold text-gray-900">{stats.totalReviews}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mb-2">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-xs text-gray-500 mb-1">{t.averageRating}</p>
              <div className="flex flex-col items-center">
                <p className="text-lg font-bold text-gray-900">{stats.averageRating}</p>
                {stats.averageRating > 0 && (
                  <div className="flex items-center gap-0.5 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-3 h-3 ${
                          star <= Math.round(stats.averageRating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                <ThumbsUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-xs text-gray-500 mb-1">Positive</p>
              <p className="text-lg font-bold text-gray-900">
                {stats.totalReviews > 0 
                  ? Math.round(((stats.ratingDistribution[5] + stats.ratingDistribution[4]) / stats.totalReviews) * 100)
                  : 0}%
              </p>
            </div>
          </div>
        </div>

        {/* Rating Distribution */}
        {stats.totalReviews > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Rating Distribution</h3>
            {renderRatingDistribution()}
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Filter */}
            <div className="flex gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              >
                <option value="all">{t.filterAll}</option>
                <option value="5star">{t.filter5Star}</option>
                <option value="4star">{t.filter4Star}</option>
                <option value="3star">{t.filter3Star}</option>
                <option value="2star">{t.filter2Star}</option>
                <option value="1star">{t.filter1Star}</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              >
                <option value="recent">{t.sortRecent}</option>
                <option value="rating">{t.sortRating}</option>
                <option value="oldest">{t.sortOldest}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-3">
          {filteredReviews.length > 0 ? (
            filteredReviews.map((review) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{review.clientName || 'Anonymous'}</h3>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(review.timestamp).toLocaleDateString()}</span>
                        {review.consultationType && (
                          <>
                            <span>•</span>
                            <span className="truncate">{review.consultationType}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {renderStars(review.rating)}
                    {review.reported && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                        {t.reported}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <p className="text-gray-700 leading-relaxed text-sm">{review.feedback}</p>
                </div>

                {/* Lawyer Reply */}
                {review.lawyerReply && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                          <Scale className="w-3 h-3 text-purple-600" />
                        </div>
                        <span className="font-medium text-purple-900 text-sm">Your Reply</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(review.lawyerReplyDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <p className="text-purple-800 text-sm">{review.lawyerReply}</p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleReply(review)}
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        {t.editReply}
                      </button>
                      <button
                        onClick={() => deleteReply(review.id)}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        {t.deleteReply}
                      </button>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {!review.lawyerReply && (
                    <button
                      onClick={() => handleReply(review)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      <Reply className="w-3.5 h-3.5" />
                      {t.reply}
                    </button>
                  )}
                  
                  {!review.reported && (
                    <button
                      onClick={() => reportReview(review)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <X className="w-3.5 h-3.5" />
                      {t.reportReview}
                    </button>
                  )}
                </div>
              </motion.div>
            ))
          ) : (
            <div className="bg-white rounded-xl p-8 text-center">
              <div className="text-4xl mb-3">⭐</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">
                {t.noReviews}
              </h3>
              <p className="text-gray-500 text-sm">
                {t.noReviewsDesc}
              </p>
            </div>
          )}
        </div>
      </div>

             {/* Reply Modal */}
       {showReplyModal && selectedReview && (
         <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
           <motion.div
             initial={{ scale: 0.9, opacity: 0 }}
             animate={{ scale: 1, opacity: 1 }}
             exit={{ scale: 0.9, opacity: 0 }}
             className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-2xl"
           >
             <div className="flex items-center justify-between mb-4">
               <h2 className="text-lg font-bold text-gray-900">{t.replyToReview}</h2>
               <button
                 onClick={() => setShowReplyModal(false)}
                 className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
               >
                 <X className="w-4 h-4 text-gray-500" />
               </button>
             </div>

             <div className="mb-4">
               <div className="bg-gray-50 rounded-lg p-3 mb-3">
                 <div className="flex items-center gap-2 mb-2">
                   <span className="font-medium text-gray-900 text-sm">{selectedReview.clientName || 'Anonymous'}</span>
                   {renderStars(selectedReview.rating)}
                 </div>
                 <p className="text-gray-700 text-xs">{selectedReview.feedback}</p>
               </div>

               <label className="block text-sm font-medium text-gray-700 mb-2">
                 {t.yourReply}
               </label>
               <textarea
                 value={replyText}
                 onChange={(e) => setReplyText(e.target.value)}
                 placeholder={t.replyPlaceholder}
                 className="w-full h-24 px-3 py-2.5 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                 maxLength={500}
               />
               <div className="flex justify-between items-center mt-1">
                 <p className="text-xs text-gray-500">
                   {replyText.length}/500 characters
                 </p>
               </div>
             </div>

             <div className="flex gap-2">
               <button
                 onClick={() => setShowReplyModal(false)}
                 className="flex-1 px-3 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
               >
                 {t.cancel}
               </button>
               <button
                 onClick={submitReply}
                 disabled={isSubmitting || !replyText.trim()}
                 className="flex-1 px-3 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-sm"
               >
                 {isSubmitting ? (
                   <>
                     <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                     Sending...
                   </>
                 ) : (
                   <>
                     <Send className="w-3.5 h-3.5" />
                     {t.sendReply}
                   </>
                 )}
               </button>
             </div>
           </motion.div>
         </div>
       )}
    </div>
  );
};

export default LawyerRatingsReviews;
